import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
    IExternalBooking,
    IGetBookingsAirResponse,
    TGetBookingResponse,
    IGetBookingsStayResponse
} from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { Yii } from "libraries/interfaces/Yii";
import { DatesService } from 'app_sasha/shared/services/dates/dates.service';
import { ApiIssueStatus } from "libraries/service/resources/booking/interfaces/ApiSearchParameters";
import { DateService } from "libraries/date/date.service";
import * as moment from 'moment';

interface ITimeLine {
    date: Date;
    displayDate: string;
    label: string;
    remaining?: number;
    class: ITimeLineClass;
    index: number;
    displayInfo: boolean;
}

interface ICursor {
    info?: ICursorInfo[];
    class: string;
    flex?: string;
}

interface ICursorInfo {
    label?: string;
    date?: string;

}

interface ITimeLineClass {
    style?: string;
    alignment?: string;
    flex?: string;
}

class Wording {
    public readonly remainingDays: string = Yii.t('wui-shared-js', ' jours restants');
}

@Component({
    selector: 'booking-time-line',
    templateUrl: './booking-time-line.component.html',
    styleUrls: [
        './booking-time-line.component.scss',
    ],
})

export class BookingTimeLineComponent implements OnChanges {

    @Input()
    public bookingData: TGetBookingResponse;

    public timeLineData: ITimeLine[] = [];
    public cursorLine: ICursor[] = [];
    public wording: Wording = new Wording();

    constructor(
        private readonly datesService: DatesService,
        private readonly dateService: DateService
    ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes.bookingData.firstChange) {
            //this.updateCursor(this.bookingData.whenLastUpdated, 'lastUpdate', Yii.t('wui-shared-js', 'Dernière mise à jour'));
            this.timeLineData = [];
            this.initTimeLine();
        } else {
            this.initTimeLine();
            this.setCursorPosition(new Date().toString(), 'today', Yii.t('wui-shared-js', 'Date du jour'));
            //this.setCursorPosition(this.bookingData.whenLastUpdated, 'lastUpdate', Yii.t('wui-shared-js', 'Dernière mise à jour'));
            this.setCursorFlex();
        }
    }

    public switchDisplay(data: ITimeLine): void {
        if (data.remaining !== null) {
            data.displayInfo = !data.displayInfo;
        }
    }

    private initTimeLine(): void {
        // 1. whenCreated
        this.fillDate(this.bookingData.whenCreated, Yii.t('wui-shared-js', "Création du dossier"));

        // 2. midOffice booking (aka agency booking) created
        const externalBookingWithDate: IExternalBooking = (this.bookingData.externalBooking || [])
            .find((externalBooking: IExternalBooking): boolean => externalBooking.origin === 'agency');
        if (externalBookingWithDate?.whenCreated) {
            this.fillDate(externalBookingWithDate.whenCreated, Yii.t('wui-shared-js', 'Création du dossier Mid-Office'));
        }

        // 3. emission
        const emissionLabel: string = this.bookingData.issueStatus === ApiIssueStatus.PartiallyIssued ?
            Yii.t('wui-shared-js', "Émission partielle") : Yii.t('wui-shared-js', "Émission");
        this.fillDate(this.bookingData.whenIssued, emissionLabel);

        const scheduled: boolean = this.bookingData.issueStatus ===
            ApiIssueStatus.NotIssued || this.bookingData.issueStatus === ApiIssueStatus.PartiallyIssued;
        this.fillDate(this.bookingData.whenToIssue, Yii.t('wui-shared-js', "Émission prévue"), scheduled);

        const completelyIssued: boolean = this.bookingData.issueStatus === ApiIssueStatus.CompletelyIssued;
        if (this.bookingData.price && this.bookingData.price.length > 0 && !completelyIssued) {
            if (this.bookingData.price[0].validity) {
                this.fillDate(this.bookingData.price[0].validity.end, Yii.t('wui-shared-js', "Expiration des tarifications"), scheduled);
            }
        }

        // 4. trip begin
        if ((this.bookingData as IGetBookingsAirResponse).sector) {
            this.fillDate((this.bookingData as IGetBookingsAirResponse).sector[0].begin, Yii.t('wui-shared-js', "Début du voyage"));
        } else {
            this.fillDate((this.bookingData as IGetBookingsAirResponse).begin, Yii.t('wui-shared-js', "Début du voyage"));
        }

        // 5. hotel whenLastToCancelWithoutPenalty
        if ((this.bookingData as IGetBookingsStayResponse).whenLastToCancelWithoutPenalty) {
            this.fillDate((this.bookingData as IGetBookingsStayResponse).whenLastToCancelWithoutPenalty, Yii.t('wui-shared-js', "Fin annulation gratuite"));
        }

        // 6. booking whenLastUpdated
        if (this.bookingData.whenLastUpdated) {
            this.fillDate(this.bookingData.whenLastUpdated, Yii.t('wui-shared-js', "Dernière mise à jour"));
        }

        // 7. trip end
        this.fillDate(this.bookingData.end, Yii.t('wui-shared-js', "Fin du voyage"));

        this.sortTimelineElementsByDate();
        this.setFirstAndLastClass();
        this.setIncoherentDateClass();
    }

    private sortTimelineElementsByDate(): void {
        this.timeLineData.sort((a: ITimeLine, b: ITimeLine) => {
            return a.date.getTime() - b.date.getTime();
        });
    }

    private fillDate(date: string, label: string, status?: boolean): void {
        if (date) {
            const itemClass: string = this.dateTypeClass(date, status);
            const formatedDate: Date = this.dateService.getStr2JsDateAndTime(date);
            const remainingDays: number = this.remainingDays(date);
            const remainingClass: string = remainingDays ? 'remaining' : null;
            this.timeLineData.push({
                date: formatedDate,
                displayDate: this.getDateAndTime(date),
                remaining: remainingDays,
                label,
                class: {style: [itemClass, remainingClass].join(' ')},
                index: this.timeLineData.length - 1,
                displayInfo: false,
            });
            this.cursorLine.push({
                class: null,
                flex: 'center',
                info: []
            }, {
                class: null,
                flex: 'center',
                info: []
            });
        }
    }

    private dateTypeClass(date: string, status?: boolean): string {
        const className: string[] = [];
        const now: Date = new Date();
        const nowTestDate: Date = new Date(this.getDateAndTime(now, false));
        if (nowTestDate.toString() === new Date(date).toString()) {
            className.push('passed');
        } else if (nowTestDate > new Date(date)) {
            if (status) {
                className.push('invalid');
            } else {
                className.push('passed');
            }
        }
        return className.join(' ');
    }

    private updateCursor(date: string, className: string, label: string): void {
        const cursorPlaced: ICursor[] = this.cursorLine.filter(
            (cursor: ICursor): boolean => cursor.class && (cursor.class.includes(className)));

        if (cursorPlaced.length > 0) {
            this.cursorLine = this.cursorLine.filter(
                (cursor: ICursor): boolean => !cursor.class || !(cursor.class && (cursor.class.includes(className))));
        } else {
            this.cursorLine = this.cursorLine.filter(
                (cursor: ICursor): boolean => !cursor.class || !(cursor.class && (cursor.class.includes(('today')))));
            this.setCursorPosition(new Date().toString(), 'today', Yii.t('wui-shared-js', 'Date du jour'));
        }

        this.setCursorPosition(date, className, label);
    }

    private setCursorPosition(date: string, className: string, label: string): void {
        let isInserted: boolean = false;
        const testDate: Date = this.dateService.getStr2JsDate(date);
        for (let i: number = 0; i <= this.timeLineData.length; i++) {
            if (this.timeLineData[i + 1]) {
                if (testDate.toString() === this.timeLineData[i].date.toString()) {
                    if (i === 0) {
                        this.setCursorInformation(i, [className, 'begin'].join(' '), label, date);
                        isInserted = true;
                    } else {
                        this.setCursorInformation((i * 2 + 1), className, label, date);
                        isInserted = true;
                    }
                } else if (testDate > new Date(this.timeLineData[i].date) && testDate < new Date(this.timeLineData[i + 1].date)) {
                    this.setCursorInformation(((i + 1) * 2), className, label, date);
                    isInserted = true;
                }
            }
        }
        if (this.timeLineData.length === 1) {
            this.setCursorInformation(1, className, label, date);
        } else if (!isInserted) {
            this.setCursorInformation(this.cursorLine.length - 1, [className, 'end'].join(" "), label, date);
        }
        this.keepLastCursor(label);
    }

    private setCursorInformation(index: number, className: string, label: string, date: string): void {
        if (this.cursorLine[index]) {
            if (this.cursorLine[index].class === null) {
                this.cursorLine[index].class = className;
            }
            this.cursorLine[index].info.push({
                label,
                date: this.getDateAndTime(date),
            });
        }
    }

    private keepLastCursor(label: string): void {
        let findCursor: boolean = false;
        for (let i: number = this.cursorLine.length - 1; i >= 0; i--) {
            if (findCursor) {
                this.cursorLine[i].class = null;
                this.cursorLine[i].info.pop();
            } else if (this.cursorLine[i].info) {
                const infoSearch: ICursorInfo = this.cursorLine[i].info.find((displayInfo: ICursorInfo) => displayInfo.label === label);
                if (infoSearch) {
                    findCursor = true;
                }
            }
        }
    }

    private remainingDays(date: string): number {
        const now: moment.Moment = moment(new Date());
        const eventDate: moment.Moment = moment(new Date(date));
        const duration: moment.Duration = moment.duration(eventDate.diff(now));
        const returnDay: number = Math.round(duration.asDays()) + 1;
        return returnDay <= 0 ? null : returnDay;
    }

    private setFirstAndLastClass(): void {
        if (this.timeLineData?.length) {
            this.timeLineData[0].class.alignment = "text begin";
            this.timeLineData[0].class.flex = "begin";

            this.timeLineData[this.timeLineData.length - 1].class.alignment = "text end";
            this.timeLineData[this.timeLineData.length - 1].class.flex = "end";

            for (let i: number = 1; i <= this.timeLineData.length - 2; i++) {
                this.timeLineData[i].class.alignment = "text center";
                this.timeLineData[i].class.flex = "center";

            }
            if (this.timeLineData.length === 1) {
                this.timeLineData[0].class.alignment = "text center";
                this.timeLineData[0].class.flex = "center";

            }
        }
    }

    private setIncoherentDateClass(): void {
        if (this.timeLineData?.length && this.timeLineData?.length > 1) {
            this.timeLineData.forEach(
                (actualItem: ITimeLine, actualIndex: number): void => {
                    const lastIndex: number = this.timeLineData.length - 1;
                    if (actualIndex < lastIndex) {
                        const nextIndex: number = actualIndex + 1;
                        const nextItem: ITimeLine = this.timeLineData[nextIndex];
                        const nextItemIsBeforeActualItem: boolean = moment(nextItem.date).isBefore(moment(actualItem.date));
                        if (nextItemIsBeforeActualItem) {
                            this.timeLineData[actualIndex].class.style += " invalid";
                            this.timeLineData[nextIndex].class.style += " invalid";
                        }
                    }
                }
            );
        }
    }

    private setCursorFlex(): void {
        this.cursorLine[0].flex = 'begin';
        this.cursorLine[1].flex = 'begin';
        this.cursorLine[this.cursorLine.length - 1].flex = 'end';
        this.cursorLine[this.cursorLine.length - 2].flex = 'end';
    }

    private getDateAndTime(date: string | Date, haveTime: boolean = true): string {
        const returnDate: string[] = [];
        returnDate.push(this.datesService.formatDate(date, 'momentMedium'));
        const time: string = this.datesService.formatDate(date, 'momentTime');
        if (
            haveTime &&
            (
                (typeof date === "string" && date.includes("T")) ||
                (typeof date === "object" && (date as Date).getTime() !== 0))
        ) {
            returnDate.push(time);
        }
        return returnDate.join(' ');
    }

}
